import { openDB } from 'idb';
import { uuidv4 } from '../lib/utils/uuid';

const databaseName = 'wm-reports';
const databaseVersion = 2;
const storeName = 'reports';

// Per-report image lifecycle within the outbox.
const IMAGE_STATE = {
    NONE: 'none',       // no image attached
    PENDING: 'pending', // image attached, not yet uploaded
    DONE: 'done',       // image uploaded successfully
    DROPPED: 'dropped'  // image permanently abandoned (e.g. retry cap reached)
};

async function openDatabase() {
    const upgrade = (db, oldVersion) => {
        if (!db.objectStoreNames.contains(storeName)) {
            const store = db.createObjectStore(storeName, { keyPath: 'id', autoIncrement: true });
            store.createIndex('uid', 'uid', { unique: false });
        } else if (oldVersion < 2) {
            // Store already exists from v1; add the uid index used for dedup lookups.
            const store = db.transaction.objectStore(storeName);
            if (!store.indexNames.contains('uid')) {
                store.createIndex('uid', 'uid', { unique: false });
            }
        }
    }

    const options = { upgrade };
    return openDB(databaseName, databaseVersion, options);
}

// Stores a report in the outbox and returns the auto-incremented record id.
// The caller is expected to have set report.uid already; if it is missing the
// record is still stored (older callers) but without idempotency guarantees.
async function storeReport(db, report, image) {
    const hasImage = !!(image && image.uri && image.type);

    let reportRecord = {
        ...report,
        attemptedCreate: false,
        serverCreated: false,
        imageState: hasImage ? IMAGE_STATE.PENDING : IMAGE_STATE.NONE,
        attempts: 0,
        imageAttempts: 0,
        lastError: null,
        storedAt: Date.now()
    }

    if (hasImage) {
        reportRecord.imageUri = image.uri;
        reportRecord.imageType = image.type;
        // Stable client-generated id so a late/retried image upload is
        // idempotent on the backend (unique per report_id) and never appends a
        // duplicate image.
        reportRecord.imageUid = uuidv4();
    }

    return db.put(storeName, reportRecord);
}

// Persists a full record (including its id) back to the store. Used by the sync
// state machine to advance stage fields between attempts.
async function updateReport(db, record) {
    return db.put(storeName, record);
}

async function getReports(db) {
   return db.getAll(storeName);
}

async function getReportByUid(db, uid) {
    if (!uid) { return undefined; }
    return db.getFromIndex(storeName, 'uid', uid);
}

async function deleteReport(db, id) {
    return db.delete(storeName, id);
}

export {
    IMAGE_STATE,
    openDatabase,
    storeReport,
    updateReport,
    getReports,
    getReportByUid,
    deleteReport
}
